/**
 * TEACHER CONTENT STUDIO — Deskey Teacher Dashboard
 * Subject-centric studio with AI framework generation and @mention chat editing
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { INBOX_KEY, useInboxPages, useLoadMoreOnScroll } from '@/components/teacher/inboxPages';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import { signedOutPath } from '@/lib/loginEndpoint';
import { useApp } from '@/context';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PRIMARY_BTN, SECONDARY_BTN, CARD, CARD_HOVER, PageHeading, SectionTitle, EmptyState, StatusBadge, CountTile } from '@/components/teacher/teacherUi';
import { classColor, classInk } from '@/components/teacher/classIdentity';
import {
  BookOpen, Mic, FileText, Plus, Send, Clock,
  CheckCircle, Edit, Trash2, ChevronRight, ChevronDown, Users,
  LogOut, Globe, Star, Brain, Layers, X,
  Target, Link2, Save, Library, Image, Menu, ChevronUp, PlayCircle,
  Settings, Eye, FolderOpen, Archive, Highlighter, Wand, ArrowDown,
  /* GraduationCap, */ ChevronLeft, Undo2, Redo2, Paperclip, Download, Film, Music, Play, Image2, Search, RefreshCw, AlertCircle
} from '@/components/DeskeyIcons';
import { sessionPill, filterSessions, SESSION_FILTERS } from '@/components/teacher/sessionFilter';

const API = process.env.REACT_APP_BACKEND_URL;
const G = '#34A853';

import { PREVIEW_THEMES } from './lib/previewThemes';
import { RenderContent, cleanText } from './components/RenderContent';
import DeskeyPaint from './components/DeskeyPaint';
import DeskeyChart from './components/DeskeyChart';
import TeacherMyClasses, { ClassDetailDrawer } from './components/TeacherMyClasses';
import PublishToLessonModal from './components/PublishToLessonModal';

// ── Table color system: presets + resolver ──
const TABLE_DEFAULT_COLORS = {
  header_bg: '#ecfdf5',
  header_text: '#065f46',
  accent: '#34A853',
  odd_row_bg: '#ffffff',
  even_row_bg: '#f8fafc',
  text: '#1e293b',
  border: '#34A853'
};

const TABLE_THEMES = [
  { id: 'emerald', label: 'Emerald', colors: { header_bg: '#ecfdf5', header_text: '#065f46', accent: '#34A853', odd_row_bg: '#ffffff', even_row_bg: '#f0fdf4', text: '#1e293b', border: '#34A853' } },
  { id: 'sapphire', label: 'Sapphire', colors: { header_bg: '#eff6ff', header_text: '#1e3a8a', accent: '#3b82f6', odd_row_bg: '#ffffff', even_row_bg: '#f0f9ff', text: '#1e293b', border: '#3b82f6' } },
  { id: 'amethyst', label: 'Amethyst', colors: { header_bg: '#faf5ff', header_text: '#6b21a8', accent: '#8b5cf6', odd_row_bg: '#ffffff', even_row_bg: '#f5f3ff', text: '#1e293b', border: '#8b5cf6' } },
  { id: 'ruby', label: 'Ruby', colors: { header_bg: '#fef2f2', header_text: '#991b1b', accent: '#dc2626', odd_row_bg: '#ffffff', even_row_bg: '#fff1f2', text: '#1e293b', border: '#dc2626' } },
  { id: 'amber', label: 'Amber', colors: { header_bg: '#fffbeb', header_text: '#78350f', accent: '#d97706', odd_row_bg: '#ffffff', even_row_bg: '#fef3c7', text: '#1e293b', border: '#d97706' } },
  { id: 'teal', label: 'Teal', colors: { header_bg: '#f0fdfa', header_text: '#134e4a', accent: '#0d9488', odd_row_bg: '#ffffff', even_row_bg: '#ccfbf1', text: '#1e293b', border: '#0d9488' } },
  { id: 'rose', label: 'Rose', colors: { header_bg: '#fff1f2', header_text: '#881337', accent: '#e11d48', odd_row_bg: '#ffffff', even_row_bg: '#ffe4e6', text: '#1e293b', border: '#e11d48' } },
  { id: 'slate', label: 'Slate', colors: { header_bg: '#f1f5f9', header_text: '#0f172a', accent: '#475569', odd_row_bg: '#ffffff', even_row_bg: '#f8fafc', text: '#1e293b', border: '#475569' } },
  { id: 'midnight', label: 'Midnight', colors: { header_bg: '#1e293b', header_text: '#f1f5f9', accent: '#38bdf8', odd_row_bg: '#0f172a', even_row_bg: '#1e293b', text: '#e2e8f0', border: '#334155' } },
];

const resolveTableColors = (table) => ({ ...TABLE_DEFAULT_COLORS, ...(table?.colors || {}) });

const api = teacherApi;

const Logo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill={G} />
  </svg>
);

// import SyllabusWorkspace from './components/SyllabusWorkspace';
import TeacherHubBrowser from './components/TeacherHubBrowser';
import EquationStudio from './components/teacher/EquationStudio';
import ImageGenHelper from './components/teacher/ImageGenHelper';
import SectionEquations from './components/teacher/SectionEquations';
import ModelPicker, { ModelBadge } from './components/ModelPicker';
import SafeImage from './components/SafeImage';
import ScientificSymbolPicker from './components/ScientificSymbolPicker';
import { FrameworkSection, AttachmentPreviewCard, AttachmentViewerModal, VisualEditorModal, ContentEditor } from './components/teacher/ContentEditor';
// import DigestSettings from './components/teacher/DigestSettings'; // digest card disabled for now
import AutoSyncMultimodalModal from './components/teacher/AutoSyncMultimodalModal';
import { isAwaitingTranscribe, recordedAtLabel } from './components/teacher/PendingAudioList';
import AutoSyncReviewPage from './components/teacher/AutoSyncReviewPage';
import SurrogateBanner from '@/components/SurrogateBanner';
import { Shield, Copy, Hash, Mail, UserPlus, ArrowLeft, Bell, MessageSquare, Calendar, Pencil, Square, Circle, ArrowRight, Type, Palette, Minus, Loader as LoaderIcon, Sigma, FunctionSquare, Atom, Beaker, FlaskConical } from 'lucide-react';
import { getSurrogateState, acceptSubstitution, enterSurrogateMode } from './lib/surrogate';
import { getSectionIcon } from './lib/sectionIcons';
import { isSmartNoteBusy, isGeneratingSession, isProcessingItem, isSkippedSession, routingLabel, studioCounts } from './lib/sessionStatus';
import { notifyError, friendlyError } from './lib/errors';
import { teacherApi } from './lib/teacherApi';
import { parseTeacherPath, teacherPath } from './lib/teacherRoutes';
import { InlineSparkle } from './components/Loader';
import { CountTilesSkeleton, SessionCardsSkeleton, ContentRowsSkeleton, ChapterListSkeleton, SessionReviewSkeleton, SubjectPageSkeleton, EditorSkeleton, SubstitutionCardsSkeleton } from './components/teacher/TeacherSkeletons';

// Section icons come from the shared registry in lib/sectionIcons.

const DIFF = { easy: { bg: '#DCFCE7', text: '#166534', l: 'Easy' }, medium: { bg: '#FEF9C3', text: '#854D0E', l: 'Medium' }, hard: { bg: '#FEE2E2', text: '#991B1B', l: 'Hard' } };

const SECTIONS_NAV = [
  { id: 'studio', label: 'Content Studio', icon: Mic },
  // Syllabus tab hidden for now — to restore, uncomment this, the render
  // branch below, and the SyllabusWorkspace + GraduationCap imports.
  // { id: 'syllabus', label: 'Syllabus', icon: GraduationCap },
  { id: 'hub', label: 'DeskeyHUB', icon: Library },
  { id: 'classes', label: 'My Classes', icon: Layers },
  { id: 'substitution', label: 'Substitution', icon: Shield },
  { id: 'published', label: 'Published', icon: CheckCircle },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// ── Session-card field preferences (saved per teacher via API; localStorage is a fast-start cache) ──
const CARD_FIELDS_KEY = 'desky_session_card_fields';
// Class & chapter always show on the card; only these extras are toggleable.
const CARD_FIELD_DEFS = [
  { key: 'subject', label: 'Subject' },
  { key: 'batch', label: 'Batch' },
  { key: 'department', label: 'Department' },
];
const DEFAULT_CARD_FIELDS = { subject: true, batch: false, department: false };
function loadCardFields() {
  try { return { ...DEFAULT_CARD_FIELDS, ...(JSON.parse(localStorage.getItem(CARD_FIELDS_KEY) || '{}')) }; }
  catch { return { ...DEFAULT_CARD_FIELDS }; }
}
// Resolve subject / batch / department for a class from the grouped-class tree.
function resolveClassMeta(classId, classGroups) {
  if (!classId) return null;
  for (const g of classGroups || []) {
    for (const b of g.batches || []) {
      const c = (b.classes || []).find(x => x.id === classId);
      if (c) return {
        subject: (c.subjects && c.subjects[0]) || '',
        batch: b.batch?.name || '',
        department: g.department?.name || '',
        color: classColor(g.department?.sticker_color, g.department?.id),
      };
    }
  }
  return null;
}

const COMMANDS = [
  { cmd: '/add', label: 'Add content', desc: 'Add new content to section' },
  { cmd: '/edit', label: 'Edit content', desc: 'Modify existing content' },
  { cmd: '/simplify', label: 'Simplify', desc: 'Make content simpler' },
  { cmd: '/expand', label: 'Expand', desc: 'Add more detail' },
  { cmd: '/examples', label: 'Add examples', desc: 'Add real-world examples' },
  { cmd: '/quiz', label: 'Add quiz', desc: 'Add practice questions' },
  { cmd: '/summarize', label: 'Summarize', desc: 'Create a summary' },
  // STEM-focused (these are interpreted by /studio/ai-chat in the backend)
  { cmd: '/equation', label: 'Insert equation', desc: 'Generate a LaTeX equation for this section' },
  { cmd: '/derive', label: 'Derive', desc: 'Show step-by-step derivation' },
  { cmd: '/solve', label: 'Solve step-by-step', desc: 'Worked solution with units' },
  { cmd: '/units', label: 'Add units & dimensions', desc: 'Annotate variables with SI units' },
  { cmd: '/balance', label: 'Balance reaction', desc: 'Balance a chemistry equation' },
  { cmd: '/reaction', label: 'Explain reaction', desc: 'Reactants → products with mechanism' },
  { cmd: '/diagram', label: 'Generate diagram', desc: 'AI image — labeled diagram' },
  { cmd: '/graph', label: 'Plot / graph', desc: 'Coordinate plot or function graph' },
  { cmd: '/board', label: 'Open board photos', desc: 'Add blackboard photos + OCR fusion' },
];

// Subject-adaptive prompt suggestions for the empty AI chat state.
// Returns 4 chips. The chips reference a section by @<title> so the AI knows what to edit.
function getSubjectSuggestions(subject, sections) {
  const first = sections?.[0]?.title || 'Overview';
  const second = sections?.[1]?.title || 'Key Concepts';
  const s = (subject || '').toLowerCase();
  if (['math', 'mathematics'].includes(s)) {
    return [
      { label: `@${first} /equation insert main formula`, type: 'math' },
      { label: `@${first} /solve a worked example`,        type: 'math' },
      { label: `@${second} /graph plot the function`,       type: 'math' },
      { label: `@${first} /derive the proof step-by-step`,  type: 'math' },
    ];
  }
  if (['physics'].includes(s)) {
    return [
      { label: `@${first} /equation Newton's 2nd law with units`, type: 'math' },
      { label: `@${first} /diagram free-body diagram`,           type: 'image' },
      { label: `@${second} /solve numerical with SI units`,       type: 'math' },
      { label: `@${first} /units list variables and SI units`,    type: 'math' },
    ];
  }
  if (['chemistry'].includes(s)) {
    return [
      { label: `@${first} /balance the chemical equation`,        type: 'chem' },
      { label: `@${first} /reaction explain mechanism`,           type: 'chem' },
      { label: `@${second} /diagram lab apparatus setup`,         type: 'image' },
      { label: `@${first} /equation rate law expression`,         type: 'chem' },
    ];
  }
  if (['biology'].includes(s)) {
    return [
      { label: `@${first} /diagram labeled cell diagram`,         type: 'image' },
      { label: `@${first} /expand with real-world examples`,      type: 'text' },
      { label: `@${second} simplify for grade 9`,                 type: 'text' },
      { label: `@${first} /quiz 5 practice questions`,            type: 'text' },
    ];
  }
  if (['computer_science', 'cs', 'programming', 'computing'].includes(s)) {
    return [
      { label: `@${first} add a code example`,                    type: 'text' },
      { label: `@${first} explain the algorithm step-by-step`,    type: 'text' },
      { label: `@${second} /diagram flowchart`,                   type: 'image' },
      { label: `@${first} /quiz 5 practice questions`,            type: 'text' },
    ];
  }
  // Default (history, english, social, etc.)
  return [
    { label: `@${first} simplify for grade 9`,                    type: 'text' },
    { label: `/expand with real-world examples`,                  type: 'text' },
    { label: `@${second} /diagram create illustration`,           type: 'image' },
    { label: `@${first} /quiz 5 practice questions`,              type: 'text' },
  ];
}

// Subject-aware quick-insert chips for the editor toolbar
function getSubjectQuickActions(subject) {
  const s = (subject || '').toLowerCase();
  if (['math', 'mathematics'].includes(s)) {
    return [
      { id: 'equation', label: 'Equation', icon: FunctionSquare, kind: 'eq' },
      { id: 'graph',    label: 'Graph',    icon: 'svg-graph',    kind: 'chat', text: '/graph plot ' },
      { id: 'solve',    label: 'Worked example', icon: 'svg-list', kind: 'chat', text: '/solve ' },
    ];
  }
  if (['physics'].includes(s)) {
    return [
      { id: 'equation', label: 'Equation',     icon: FunctionSquare, kind: 'eq' },
      { id: 'diagram',  label: 'Diagram',      icon: 'svg-graph',    kind: 'img' },
      { id: 'units',    label: 'Units',        icon: 'svg-list',     kind: 'chat', text: '/units ' },
    ];
  }
  if (['chemistry'].includes(s)) {
    return [
      { id: 'reaction', label: 'Balance reaction', icon: FlaskConical, kind: 'eq' },
      { id: 'mechanism',label: 'Mechanism',        icon: Beaker,       kind: 'chat', text: '/reaction ' },
      { id: 'diagram',  label: 'Lab setup',        icon: 'svg-graph',  kind: 'img' },
    ];
  }
  if (['biology'].includes(s)) {
    return [
      { id: 'diagram',  label: 'Diagram',  icon: 'svg-graph', kind: 'img' },
      { id: 'process',  label: 'Process',  icon: 'svg-list',  kind: 'chat', text: '/expand explain process step-by-step ' },
    ];
  }
  return [];
}

/* ═══════════════════════════════════════════════════════════════════════════
   TOP-RIGHT AVATAR MENU — replaces the old bottom-of-sidebar avatar/name/
   logout block. Click the avatar for a small overlay card with name, email,
   the "Teacher" role badge, and a themed logout action (confirm-gated).
   ═══════════════════════════════════════════════════════════════════════════ */
function TeacherAvatarMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initials = (user?.full_name || 'T').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const handleLogoutClick = async () => {
    setOpen(false);
    const ok = await confirmDialog({ title: 'Log out', message: 'You’ll need to sign in again to access Deskey Teacher.', confirmLabel: 'Log out', danger: true });
    if (ok) onLogout();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] transition-[background-color,transform] active:scale-95"
        title={user?.full_name}
        data-testid="teacher-avatar-menu-trigger"
      >
        {initials}
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+8px)] w-64 rounded-xl bg-white border border-slate-200 shadow-lg overflow-hidden z-50"
          data-testid="teacher-avatar-menu-panel"
        >
          <div className="p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium text-white bg-[#1A7F37] flex-shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name || 'Teacher'}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              <p className="mt-1 text-xs text-slate-500">Teacher</p>
            </div>
          </div>
          <div className="h-px bg-slate-100" />
          <button
            onClick={handleLogoutClick}
            className="w-full flex items-center gap-2 px-4 h-11 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
            data-testid="teacher-avatar-menu-logout"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */
export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { logout } = useApp();
  const location = useLocation();
  // Which tab / session / review / subject is open lives in the URL (see
  // lib/teacherRoutes), so the browser Back button steps back one page inside
  // the dashboard instead of leaving it for the institute login page.
  const route = parseTeacherPath(location.pathname);
  const { section } = route;
  const [dashboard, setDashboard] = useState(null);
  const [activeDraft, setActiveDraft] = useState(null);     // data for route.draftId
  const [reviewSession, setReviewSession] = useState(null); // data for route.reviewId (Auto Sync review)
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoaded, setSubjectsLoaded] = useState(false);
  const activeSubject = route.subjectId ? (subjects.find(s => s.id === route.subjectId) || null) : null;
  const [subCount, setSubCount] = useState(0);
  const [cardFields, setCardFields] = useState(loadCardFields);
  const updateCardFields = (patch) => {
    const next = { ...cardFields, ...patch };
    setCardFields(next);
    try { localStorage.setItem(CARD_FIELDS_KEY, JSON.stringify(next)); } catch { /* ignore storage errors */ }
    api('/preferences/session-card', { method: 'PUT', body: JSON.stringify({ fields: next }) })
      .then(r => { if (r?.detail) toast.error('Could not save card settings'); })
      .catch(() => toast.error('Could not save card settings'));
  };
  // Hydrate card-field prefs from the server so they follow the teacher across
  // devices — only on the tabs that use them (studio cards, Settings).
  const needsCardPrefs = section === 'studio' || section === 'settings';
  const [cardPrefsLoaded, setCardPrefsLoaded] = useState(false);
  useEffect(() => {
    if (!needsCardPrefs || cardPrefsLoaded) return;
    setCardPrefsLoaded(true);
    api('/preferences/session-card').then(r => {
      if (r?.fields) {
        const next = { ...DEFAULT_CARD_FIELDS, ...r.fields };
        setCardFields(next);
        try { localStorage.setItem(CARD_FIELDS_KEY, JSON.stringify(next)); } catch { /* ignore storage errors */ }
      }
    }).catch(() => { /* offline — keep the local cache */ });
  }, [needsCardPrefs, cardPrefsLoaded]);

  const user = JSON.parse(localStorage.getItem('desky_user') || '{}');
  const token = localStorage.getItem('desky_token');

  // Data loads for what is on screen, not all at login: the header counts
  // (dashboard) fill in without blocking the page, subjects load the first
  // time Content Studio is shown, and a session loads only when opened.
  const loadDashboard = useCallback(async () => {
    try { setDashboard(await api('/studio/dashboard')); }
    catch (e) { notifyError(e, "Couldn't load your dashboard. Please refresh the page."); }
  }, []);
  const loadSubjects = useCallback(async () => {
    try { const r = await api('/studio/subjects'); setSubjects(r.subjects || []); }
    catch { /* studio home still renders; subjects just stay empty */ }
    setSubjectsLoaded(true);
  }, []);
  // Refresh whatever is on screen after a create/edit/delete.
  const reload = useCallback(() => {
    if (section !== 'studio') return;
    loadDashboard();
    loadSubjects();
  }, [loadDashboard, loadSubjects, section]);

  // Navigation — opening something pushes a URL so Back returns here; the
  // in-page "back" buttons go back one step, or to the studio home when the
  // page was opened directly (no history inside the app to go back to).
  const goBackOr = (fallbackPath) => {
    if (location.key !== 'default') navigate(-1);
    else navigate(fallbackPath, { replace: true });
  };
  const openDraft = (draft) => {
    if (!draft?.id) return;
    setActiveDraft(draft);
    navigate(teacherPath.session(draft.id));
  };
  const openReview = (item) => {
    if (!item?.id) return;
    setReviewSession(item);
    navigate(teacherPath.review(item.id));
  };
  const openSubject = (subject) => { if (subject?.id) navigate(teacherPath.subject(subject.id)); };

  // The sidebar's Substitution badge is filled in by the Substitution tab's own
  // fetch (setSubCount) — nothing is requested for it at login. New
  // substitutions still announce themselves through the notification bell.

  // Studio data (header counts, classes for the editor, subjects) loads the
  // first time a Content Studio page is shown — never on other tabs.
  const [dashboardRequested, setDashboardRequested] = useState(false);
  useEffect(() => {
    if (section !== 'studio' || dashboardRequested) return;
    setDashboardRequested(true);
    loadDashboard();
  }, [section, dashboardRequested, loadDashboard]);
  useEffect(() => { if (section === 'studio' && !subjectsLoaded) loadSubjects(); }, [section, subjectsLoaded, loadSubjects]);
  useEffect(() => { if (activeDraft) setSidebarOpen(false); else setSidebarOpen(true); }, [activeDraft]);

  // Session in the URL → its data. Already in hand when opened from a card;
  // fetched on refresh / direct link.
  useEffect(() => {
    const id = route.draftId;
    if (!id) { setActiveDraft(null); return undefined; }
    if (activeDraft?.id === id) return undefined;
    let cancelled = false;
    api(`/studio/drafts/${id}`).then(r => {
      if (cancelled) return;
      if (r?.id) setActiveDraft(r);
      else { toast.error('Session not found'); navigate(teacherPath.section('studio'), { replace: true }); }
    }).catch(e => { if (!cancelled) notifyError(e, "Couldn't open this session."); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.draftId]);

  // Auto Sync review in the URL → its inbox item.
  useEffect(() => {
    const id = route.reviewId;
    if (!id) { setReviewSession(null); return undefined; }
    if (reviewSession?.id === id) return undefined;
    let cancelled = false;
    api(`/audio-inbox/${id}`).then(r => {
      if (cancelled) return;
      if (r?.item) setReviewSession(r.item);
      else navigate(teacherPath.section('studio'), { replace: true });
    }).catch(e => { if (!cancelled) notifyError(e, "Couldn't open this session review."); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.reviewId]);

  // A subject id that no longer exists → back to the studio home.
  useEffect(() => {
    if (route.subjectId && subjectsLoaded && !activeSubject) navigate(teacherPath.section('studio'), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.subjectId, subjectsLoaded, activeSubject]);

  // Old links: /teacher?draft=<id> (e.g. from My Classes) and bare /teacher
  // are rewritten to their real URLs without adding a history entry.
  useEffect(() => {
    const draftId = new URLSearchParams(location.search).get('draft');
    if (draftId) navigate(teacherPath.session(draftId), { replace: true });
    else if (location.pathname.replace(/\/+$/, '') === '/teacher') navigate(teacherPath.section('studio'), { replace: true });
  }, [location.pathname, location.search, navigate]);

  const stats = dashboard?.stats || {};
  const classes = dashboard?.classes || [];

  // Clearing the app's signed-in user (not just localStorage) is what stops
  // the browser Back button from reopening the dashboard after logging out.
  const handleLogout = () => { logout(); navigate(signedOutPath(), { replace: true }); };

  return (
    <div className="h-screen flex bg-[#f8f9fa] overflow-hidden" data-testid="teacher-dashboard">
      {/* In the content editor the sidebar opens as a drawer. The editor's
          toolbar is sticky at z-50, so the dimming overlay and the drawer sit
          above it (z-60 / z-70) — otherwise the toolbar paints over both. */}
      {/* The drawer slides (transform) so its nav moves with the panel, and the
          overlay fades over the same 300ms. Outside the editor the sidebar
          pushes the page, so it animates its width — always clipped, or the
          full nav would pop in before the panel has grown. */}
      {activeDraft && (
        <div
          className={`fixed inset-0 z-[60] bg-black/30 transition-opacity duration-300 ease-in-out ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
          data-testid="sidebar-overlay" />
      )}

      <aside className={`teacher-sidebar flex-shrink-0 bg-[#1a1f36] flex flex-col overflow-hidden duration-300 ease-in-out ${activeDraft
        ? `fixed inset-y-0 left-0 z-[70] w-60 transition-transform ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`
        : `relative z-40 transition-[width] ${sidebarOpen ? 'w-60' : 'w-0'}`}`}
        aria-hidden={!sidebarOpen}
        data-testid="teacher-sidebar">
        <div className="w-60 h-full flex flex-col">
          {/* Same height as the top bar so both bottom borders line up */}
          <div className="h-14 px-4 flex items-center gap-2 border-b border-white/10 flex-shrink-0">
            <Logo size={24} /><span className="text-base font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Deskey</span>
            <span className="ml-auto text-xs font-medium text-white/50">Studio</span>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {SECTIONS_NAV.map(s => (
              <button key={s.id} onClick={() => {
                  const path = teacherPath.section(s.id);
                  if (location.pathname !== path) navigate(path);
                  setSidebarOpen(true);
                }}
                className={`sidebar-nav-item ${section === s.id ? 'active' : ''}`} data-testid={`nav-${s.id}`}>
                <s.icon />
                <span>{s.label}</span>
                {s.id === 'substitution' && subCount > 0 && (
                  <span className="ml-auto min-w-5 h-5 px-1.5 rounded-md bg-white/10 text-white text-xs font-medium tabular-nums flex items-center justify-center">{subCount}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Scoped to the teacher's own dashboard only (this component is
            only reachable at /teacher/* behind ProtectedRoute) — renders
            when THIS teacher is inside a Surrogate Mode session, or has an
            accepted, in-window substitution ready to enter. Other
            teachers, students, admins, and the public landing/login pages
            never mount this component at all. */}
        <SurrogateBanner />
        {/* Each page carries its own heading, so the top bar holds only the
            menu toggle, back link, counts, bell and account. */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-6 gap-4 flex-shrink-0">
          {!sidebarOpen && <button onClick={() => setSidebarOpen(true)} aria-label="Open menu" className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors" data-testid="sidebar-toggle"><Menu className="w-5 h-5" /></button>}
          {activeSubject && !activeDraft && <button onClick={() => goBackOr(teacherPath.section('studio'))} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Back to subjects</button>}
          <div className="ml-auto flex items-center gap-4">
            {dashboard && <div className="text-sm text-slate-500 tabular-nums">
              <span className="text-slate-900 font-medium">{stats.total_classes || 0}</span> {plural(stats.total_classes || 0, 'class', 'classes')}
              <span className="mx-2 text-slate-300">·</span>
              {/* Server-side count: studio subjects UNION class-assigned ones.
                  `subjects` here is only the studio-authored list, which is 0
                  for teachers who just teach class subjects. */}
              <span className="text-slate-900 font-medium">{stats.total_subjects ?? subjects.length}</span> {plural(stats.total_subjects ?? subjects.length, 'subject')}
            </div>}
            <NotificationBell token={token} />
            <TeacherAvatarMenu user={user} onLogout={handleLogout} />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {section === 'studio' && !route.draftId && !route.reviewId && !route.subjectId && (
            <StudioHome subjects={subjects} setSubjects={setSubjects} setActiveSubject={openSubject} setActiveDraft={openDraft} setReviewSession={openReview} stats={stats} reload={reload} cardFields={cardFields} />
          )}
          {section === 'studio' && route.reviewId && (reviewSession ? (
            <AutoSyncReviewPage
              session={reviewSession}
              goBack={() => goBackOr(teacherPath.section('studio'))}
              onGenerated={(draft) => { setActiveDraft(draft); navigate(teacherPath.session(draft.id), { replace: true }); reload(); }}
            />
          ) : <SessionReviewSkeleton />)}
          {section === 'studio' && route.subjectId && (activeSubject ? (
            <SubjectView subject={activeSubject} drafts={NO_DRAFTS} setActiveDraft={openDraft} classes={classes} reload={reload} />
          ) : <SubjectPageSkeleton />)}
          {section === 'studio' && route.draftId && (activeDraft ? (
            <ContentEditor draft={activeDraft} setDraft={setActiveDraft} classes={classes} reload={reload} goBack={() => { goBackOr(teacherPath.section('studio')); reload(); }} />
          ) : <EditorSkeleton />)}
          {section === 'classes' && <TeacherMyClasses />}
          {section === 'substitution' && <SubstitutionSection token={token} onActiveCount={setSubCount} />}
          {section === 'published' && <PublishedContent />}
          {/* {section === 'syllabus' && <SyllabusWorkspace />} */}
          {section === 'hub' && <TeacherHubBrowser />}
          {section === 'settings' && <SettingsSection fields={cardFields} onChange={updateCardFields} />}
        </div>
      </main>
    </div>
  );
}

// SubjectView fetches its own sessions; this is only its fallback (stable, so
// its effect doesn't refetch on every dashboard re-render).
const NO_DRAFTS = [];

/* ═══════════════════════════════════════════════════════════════════════════
   STUDIO HOME — Subject Cards
   ═══════════════════════════════════════════════════════════════════════════ */

const SEARCH_DEBOUNCE_MS = 500;

function StudioHome({ subjects, setSubjects, setActiveSubject, setActiveDraft, setReviewSession, stats, reload, cardFields }) {
  const [routeFor, setRouteFor] = useState(null); // item being routed to a class + chapter
  const [opening, setOpening] = useState(null);
  const [autoSyncOpen, setAutoSyncOpen] = useState(false);
  const [autoSyncFor, setAutoSyncFor] = useState(null); // pending session preselected in Auto Sync
  const [sessionFilter, setSessionFilter] = useState('all');
  const [sessionQuery, setSessionQuery] = useState('');
  const queryClient = useQueryClient();
  const searchText = useDebouncedValue(sessionQuery.trim(), SEARCH_DEBOUNCE_MS);
  // Unfiltered list: count tiles + Screening, so they don't change while searching.
  // Skipped sessions stay listed (tagged "Skipped") but aren't counted.
  const inboxQ = useInboxPages('all', '');
  // What the grid shows: the chosen tab + search, run on the server over every session.
  const viewQ = useInboxPages(sessionFilter, searchText);
  const loadMoreRef = useLoadMoreOnScroll(viewQ);
  const items = inboxQ.items;
  const loading = inboxQ.isPending;
  const loadInbox = () => queryClient.invalidateQueries({ queryKey: INBOX_KEY });
  const setItems = (update) => queryClient.setQueriesData({ queryKey: INBOX_KEY }, (old) => old && ({
    ...old, pages: old.pages.map(page => ({ ...page, items: update(page.items) })),
  }));

  const classGroupsQ = useQuery({
    queryKey: ['teacher', 'classes', 'grouped-all'],
    queryFn: () => api('/classes/grouped').then(r => r?.groups || []).catch(() => []),
  });
  const classGroups = classGroupsQ.data ?? [];

  // Processing just finished — pick up drafts auto-created by the pipeline.
  const wasProcessing = useRef(false);
  useEffect(() => {
    const hasProcessing = items.some(isProcessingItem);
    if (wasProcessing.current && !hasProcessing) reload();
    wasProcessing.current = hasProcessing;
  }, [items, reload]);


  const handleOpen = async (item) => {
    if (item.status === 'error') { notifyError(item.error, 'Processing failed. Retry or delete.'); return; }
    // Synced but not transcribed: add boards / reference, then Transcribe.
    if (isAwaitingTranscribe(item)) { setAutoSyncFor(item.id); setAutoSyncOpen(true); return; }
    if (isGeneratingSession(item)) { setReviewSession(item); return; }
    if (isSmartNoteBusy(item)) {
      toast('Smart Note still processing…', { description: 'Voice and boards are being fused — a moment more.' });
      return;
    }
    if (item.status !== 'ready' && item.status !== 'drafted' && item.status !== 'published') {
      toast('Still transcribing…', { description: 'Give it a moment — this usually takes 20-60s.' });
      return;
    }
    // Auto Sync multimodal sessions without a draft yet open the review page:
    // transcript + board text side by side, then an explicit "Generate Session".
    if (item.auto_sync_multimodal && !item.draft_id) {
      setReviewSession(item);
      return;
    }
    setOpening(item.id);
    try {
      const r = await api(`/audio-inbox/${item.id}/generate-draft`, { method: 'POST' });
      // Never open a stub: with no framework the editor offers "Generate",
      // which would overwrite the saved one.
      const draft = await api(`/studio/drafts/${r.draft_id}`);
      setActiveDraft(draft);
      loadInbox();
      if (!r.existing) reload(); // a fresh draft was created — refresh the Drafts count
    } catch (e) { notifyError(e, "Couldn't open this session. Please try again."); }
    setOpening(null);
  };

  // One-step routing: class (reassign-class) + chapter (set-chapter) together.
  // `sel` is { class_id, lesson_id?, lesson_title? }; class_id null clears routing.
  const handleRoute = async (item, sel) => {
    try {
      if (sel.class_id !== (item.detected_class_id || null)) {
        await api(`/audio-inbox/${item.id}/reassign-class`, { method: 'POST', body: JSON.stringify({ class_id: sel.class_id }) });
      }
      if (sel.class_id && (sel.lesson_id || sel.lesson_title)) {
        const r = await api(`/audio-inbox/${item.id}/set-chapter`, {
          method: 'POST',
          body: JSON.stringify({ lesson_id: sel.lesson_id || null, lesson_title: sel.lesson_title || null }),
        });
        toast.success(`Filed under “${r.lesson_name || 'chapter'}”`);
      } else {
        toast.success(sel.class_id ? 'Class updated' : 'Routing cleared');
      }
      setRouteFor(null);
    } catch (e) {
      notifyError(e, 'Failed');
    }
    loadInbox(); // also shows a class change that went through before a chapter error
  };

  const handleDelete = async (item) => {
    const msg = item.draft_id ? 'Delete this session? Its draft will be deleted too.' : 'Delete this audio from inbox?';
    if (!(await confirmDialog({ title: 'Delete session', message: msg, confirmLabel: 'Delete', danger: true }))) return;
    try {
      await api(`/audio-inbox/${item.id}`, { method: 'DELETE' });
      toast.success('Removed');
      setItems(prev => prev.filter(i => i.id !== item.id));
      reload(); // refresh the Drafts / Published counts
    } catch (e) { notifyError(e, 'Failed'); }
  };

  // Recordings not transcribed yet aren't sessions — they're reached only
  // through Screening (the button badge counts them), never the sessions grid.
  const awaitingItems = items.filter(isAwaitingTranscribe);
  const sessionItems = items.filter(item => !isAwaitingTranscribe(item));

  // Same four tiles + rules as teacher-mobile's Studio screen (lib/sessionStatus).
  const counts = studioCounts(sessionItems, stats);
  // Server already searched; this trims the tab's exact badge rules (its status match is coarse).
  const visibleSessions = filterSessions(viewQ.items.filter(item => !isAwaitingTranscribe(item)), sessionFilter, '');

  const handleRetry = async (item) => {
    try {
      await api(`/audio-inbox/${item.id}/retry`, { method: 'POST' });
      toast.success('Retrying');
      loadInbox();
    } catch (e) { notifyError(e, 'Failed'); }
  };

  const countTiles = [
    { label: 'Synced', val: counts.synced, tone: 'neutral', icon: Download },
    { label: 'Processing', val: counts.processing, tone: 'working', icon: Clock },
    { label: 'Drafts', val: counts.drafts, tone: 'todo', icon: FileText },
    { label: 'Published', val: counts.published, tone: 'done', icon: CheckCircle },
  ];

  return (
    <div className="px-8 py-8 max-w-7xl mx-auto space-y-8">
      <PageHeading
        title="Content Studio"
        description="Deskey transcribes each lecture, routes it to the right class, and lets you review and publish it as a lesson"
        action={
          <Button onClick={() => { setAutoSyncFor(null); setAutoSyncOpen(true); }} className={PRIMARY_BTN} data-testid="audio-upload-btn">
            <Mic className="w-4 h-4 mr-2" />Screening
            {awaitingItems.length > 0 && (
              <span className="ml-2 min-w-5 h-5 px-1.5 rounded-md bg-white/20 text-white text-xs font-medium tabular-nums inline-flex items-center justify-center" title={`${awaitingItems.length} ${plural(awaitingItems.length, 'recording')} waiting to be transcribed`} data-testid="screening-pending-count">
                {awaitingItems.length}
              </span>
            )}
          </Button>
        }
      />

      {/* Counts — each tile wears the same colour its sessions' status uses below */}
      {loading ? <CountTilesSkeleton /> : <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {countTiles.map((t) => <CountTile key={t.label} {...t} />)}
      </div>}

      {/* Deskey Sessions */}
      <div data-testid="audio-inbox">
        {/* Last-Synced-Lesson digest — disabled for now */}
        {/* <DigestSettings api={api} /> */}
        <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
          <SectionTitle title="Deskey Sessions" hint="Most recent first" />
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input type="search" value={sessionQuery} onChange={(e) => setSessionQuery(e.target.value)} placeholder="Search sessions" aria-label="Search sessions"
              className="w-full h-10 pl-11 pr-4 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#34A853]/40 focus:border-[#34A853]" data-testid="session-search" />
          </div>
        </div>
        {sessionItems.length > 0 && (
          <div className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100 mb-5" role="group" aria-label="Filter sessions">
            {SESSION_FILTERS.map(f => (
              <button key={f.id} type="button" onClick={() => setSessionFilter(f.id)} aria-pressed={sessionFilter === f.id}
                className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors ${sessionFilter === f.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid={`session-filter-${f.id}`}>{f.label}</button>
            ))}
          </div>
        )}
        {loading ? (
          <SessionCardsSkeleton />
        ) : sessionItems.length === 0 && awaitingItems.length > 0 ? (
          <EmptyState
            onClick={() => { setAutoSyncFor(null); setAutoSyncOpen(true); }}
            testId="audio-inbox-awaiting-only"
            title="No sessions yet"
            body={`${awaitingItems.length} ${plural(awaitingItems.length, 'recording is', 'recordings are')} waiting in Screening. Add board photos and transcribe to create a session.`}
            action="Open Screening"
          />
        ) : sessionItems.length === 0 ? (
          <EmptyState
            onClick={() => setAutoSyncOpen(true)}
            testId="audio-inbox-empty"
            icon={Mic}
            title="No Deskey Sessions synced yet"
            body="Pair your Deskey device. Lectures come in, get transcribed, and go to the matching class."
            action="Open Screening"
          />
        ) : (
          visibleSessions.length === 0 ? (
            viewQ.isPlaceholderData || viewQ.hasNextPage
              ? <SessionCardsSkeleton />
              : <EmptyState icon={Search} title="No sessions match" body="Try another tab or a different search" testId="session-filter-empty" />
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5">
            {visibleSessions.map(item => (
              <AudioInboxCard key={item.id}
                item={item} classGroups={classGroups} fields={cardFields}
                opening={opening === item.id}
                onOpen={() => handleOpen(item)}
                onRoute={() => setRouteFor(item)}
                onDelete={() => handleDelete(item)}
                onRetry={() => handleRetry(item)}
              />
            ))}
          </div>
          )
        )}
        {/* End of the list: scrolling here loads the next 50 */}
        {!loading && viewQ.hasNextPage && <div ref={loadMoreRef} aria-hidden="true" className="h-px" />}
        {viewQ.isFetchingNextPage && (
          <div role="status" className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500" data-testid="sessions-loading-more">
            <LoaderIcon className="w-4 h-4 animate-spin" />Loading more sessions…
          </div>
        )}
        {viewQ.isFetchNextPageError && (
          <div className="flex items-center justify-center gap-3 py-6 text-sm text-slate-600">
            Couldn't load more sessions.
            <button type="button" onClick={() => viewQ.fetchNextPage()} className="h-10 px-4 rounded-lg border border-slate-200 bg-white font-medium hover:bg-slate-50">Try again</button>
          </div>
        )}
      </div>

      {/* Class & chapter picker — route the session in one step */}
      {routeFor && (
        <ClassChapterPickerModal
          item={routeFor}
          classGroups={classGroups}
          onClose={() => setRouteFor(null)}
          onSubmit={(sel) => handleRoute(routeFor, sel)}
        />
      )}

      {/* Screening popup (Auto Sync multimodal) — the only place not-yet-transcribed recordings appear */}
      <AutoSyncMultimodalModal
        open={autoSyncOpen}
        onClose={() => { setAutoSyncOpen(false); setAutoSyncFor(null); }}
        onQueued={() => loadInbox()}
        onDeleted={() => loadInbox()}
        pendingItems={awaitingItems}
        initialSessionId={autoSyncFor} />
    </div>
  );
}

/* ───────────── Audio Inbox card ───────────── */
// The teacher's title wins, then the hint typed at capture time, then the class name; never the filename.
function sessionTitle(item) {
  return item.title || item.title_hint || item.detected_class_name || 'Deskey Session';
}

// Department · batch · class · subject · chapter on one line; amber when the session still needs a class.
function RoutingRow({ item, hasClass, color, placement, onRoute }) {
  const route = (e) => { e.stopPropagation(); onRoute(); };
  if (!hasClass) {
    return (
      <div className="rounded-xl bg-orange-50 pl-4 pr-1.5 h-14 flex items-center gap-3">
        <BookOpen className="w-5 h-5 text-amber-800 flex-shrink-0" />
        <p className="flex-1 min-w-0 truncate text-sm font-medium text-amber-800">No class yet</p>
        <button onClick={route} className="h-10 px-4 rounded-lg bg-white border border-amber-200 text-sm font-medium text-amber-800 hover:bg-amber-50 transition-colors flex-shrink-0" data-testid={`audio-route-${item.id}`}>Pick class &amp; chapter</button>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-slate-50 px-4 py-3 min-h-14 flex items-center gap-3">
      <BookOpen className="w-5 h-5 flex-shrink-0" style={{ color: color ? classInk(color) : '#1d4ed8' }} />
      <p className="flex-1 min-w-0 line-clamp-2 text-sm font-medium text-slate-900">
        {placement}
        {item.detected_lesson_id
          ? <> · {item.detected_lesson_name}</>
          : <span className="text-amber-700"> · no chapter yet</span>}
      </p>
      <button onClick={route} className="text-sm font-medium text-[#1A7F37] hover:underline underline-offset-4 flex-shrink-0" data-testid={`audio-route-${item.id}`}>Change</button>
    </div>
  );
}

function AudioInboxCard({ item, classGroups, fields = DEFAULT_CARD_FIELDS, opening, onOpen, onRoute, onDelete, onRetry }) {
  const smartNoteBusy = isSmartNoteBusy(item);
  const needsReview = item.auto_sync_multimodal && !item.draft_id && !smartNoteBusy;
  const generatingSession = isGeneratingSession(item);
  const awaitingTranscribe = isAwaitingTranscribe(item);
  const pill = sessionPill(item);
  const canOpen = awaitingTranscribe || generatingSession || (!smartNoteBusy && (item.status === 'ready' || item.status === 'drafted' || item.status === 'published'));
  const syncedAgo = relativeTime(item.last_synced_at || item.updated_at || item.created_at);
  const hasClass = !!item.detected_class_id;
  const classMeta = resolveClassMeta(item.detected_class_id, classGroups);
  // Order: department → batch → class → subject (chapter is appended by RoutingRow).
  // Subject / batch / department follow the Settings toggles; they come only from the routed class.
  const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1);
  const placement = [
    fields.department && classMeta?.department && cap(classMeta.department),
    fields.batch && classMeta?.batch && cap(classMeta.batch),
    item.detected_class_name,
    fields.subject && classMeta?.subject && cap(classMeta.subject),
  ].filter(Boolean).join(' · ');
  const isError = item.status === 'error';
  const isPublished = item.status === 'published';
  // Work still to do gets the filled button; finished or in-flight sessions get the outlined one.
  const needsWork = canOpen && !isPublished && !generatingSession;
  const openLabel = awaitingTranscribe ? 'Add boards and transcribe' : isPublished ? 'View session' : generatingSession ? 'View progress' : needsReview ? 'Review and generate' : 'Edit and publish';
  const OpenIcon = isPublished || generatingSession ? Eye : Edit;
  const ACTION_BTN = 'flex-1 flex items-center gap-2 justify-center';
  return (
    <div
      className={`${CARD} p-6 flex flex-col group ${canOpen ? `${CARD_HOVER} cursor-pointer` : ''}`}
      onClick={canOpen ? onOpen : undefined}
      data-testid={`audio-card-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-lg font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{sessionTitle(item)}</p>
          <p className="mt-1 text-sm text-slate-500 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-slate-400" />
            {awaitingTranscribe ? `Recorded ${recordedAtLabel(item)}` : `Synced ${syncedAgo}`}
          </p>
        </div>
        <StatusBadge tone={pill.tone} label={pill.label} />
      </div>

      <div className="mt-4">
        {isError ? (
          <div className="rounded-xl bg-red-50 px-4 py-3 flex items-start gap-3 text-sm text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-px" />
            <p className="line-clamp-2">{friendlyError(item.error, 'This recording could not be processed')}</p>
          </div>
        ) : (
          <RoutingRow item={item} hasClass={hasClass} color={hasClass ? classMeta?.color : null} placement={placement} onRoute={onRoute} />
        )}
      </div>

      <div className="mt-auto pt-6 flex items-center gap-3">
        {canOpen ? (
          <button onClick={(e) => { e.stopPropagation(); onOpen(); }} disabled={opening} className={`${needsWork ? PRIMARY_BTN : SECONDARY_BTN} ${ACTION_BTN}`} data-testid={`audio-open-${item.id}`}>
            {opening ? 'Opening…' : (<><OpenIcon className="w-4 h-4" /> {openLabel}</>)}
          </button>
        ) : isError ? (
          <button onClick={(e) => { e.stopPropagation(); onRetry(); }} className={`${SECONDARY_BTN} ${ACTION_BTN}`} data-testid={`audio-retry-${item.id}`}>
            <RefreshCw className="w-4 h-4" /> Retry sync
          </button>
        ) : (
          <div className="h-10 flex-1 rounded-lg bg-amber-50 text-sm font-medium text-amber-800 flex items-center justify-center gap-2">
            <InlineSparkle size={18} label={smartNoteBusy ? 'Combining voice and boards' : 'Transcribing session'} />
            {smartNoteBusy ? 'Combining voice and boards…' : 'Transcribing session…'}
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          aria-label="Delete session"
          title="Delete"
          className="w-10 h-10 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 flex items-center justify-center flex-shrink-0 transition-colors"
          data-testid={`audio-delete-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function ClassChapterPickerModal({ item, classGroups, onClose, onSubmit }) {
  // Flatten the dept › batch › class tree into pickable rows
  const classOptions = useMemo(() => {
    const out = [];
    for (const g of classGroups || []) {
      for (const b of g.batches || []) {
        for (const c of b.classes || []) {
          out.push({ id: c.id, name: c.name, context: [b.batch?.name, g.department?.name].filter(Boolean).join(' · ') });
        }
      }
    }
    return out;
  }, [classGroups]);

  const [selectedClassId, setSelectedClassId] = useState(item.detected_class_id || null);
  const [loading, setLoading] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [mode, setMode] = useState('pick'); // 'pick' | 'new'
  const [selectedLessonId, setSelectedLessonId] = useState(item.detected_lesson_id || null);
  const [newTitle, setNewTitle] = useState('');
  const [saving, setSaving] = useState(false);

  // Reload chapters whenever the selected class changes
  useEffect(() => {
    if (!selectedClassId) { setChapters([]); setSelectedLessonId(null); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      const r = await api(`/classes/${selectedClassId}/lessons`).catch(() => ({}));
      if (!alive) return;
      const list = r.lessons || [];
      setChapters(list);
      const keep = selectedClassId === item.detected_class_id ? item.detected_lesson_id : null;
      if (keep && list.some(c => c.id === keep)) {
        setMode('pick'); setSelectedLessonId(keep);
      } else if (list.length > 0) {
        setMode('pick'); setSelectedLessonId(list[0].id);
      } else {
        setMode('new'); setSelectedLessonId(null);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [selectedClassId, item.detected_class_id, item.detected_lesson_id]);

  const chapterChoice = mode === 'pick'
    ? (selectedLessonId ? { lesson_id: selectedLessonId } : null)
    : (newTitle.trim() ? { lesson_title: newTitle.trim() } : null);
  const canSubmit = !!selectedClassId && !!chapterChoice;
  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    await onSubmit({ class_id: selectedClassId, ...chapterChoice });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 !m-0 z-[72] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="class-chapter-picker-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900">Route this session</h3>
            <p className="text-sm text-slate-600 truncate">Students see the session in this class and chapter</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-900 p-2 rounded-lg hover:bg-slate-100 transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Class */}
          <div>
            <p className="text-sm font-medium text-slate-900 mb-2">Class</p>
            {classOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-600 bg-slate-50 rounded-lg border border-dashed border-slate-200">No classes assigned yet</div>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                {classOptions.map(c => {
                  const active = selectedClassId === c.id;
                  return (
                    <button key={c.id} onClick={() => setSelectedClassId(c.id)}
                      className={`w-full flex items-center gap-3 rounded-lg border px-3 h-14 text-left transition-colors ${active ? 'border-[#34A853] bg-[#34A853]/5' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                      data-testid={`route-class-${c.id}`}>
                      {active ? <CheckCircle className="w-4 h-4 text-[#1A7F37] flex-shrink-0" /> : <Layers className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                        {c.context && <p className="text-xs text-slate-500 truncate">{c.context}</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-900">Chapter</p>
            <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
              <button onClick={() => setMode('pick')} disabled={chapters.length === 0}
                className={`text-sm font-medium px-3 h-7 rounded transition-colors ${mode === 'pick' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'} disabled:opacity-40`}
                data-testid="chapter-mode-pick">Pick existing</button>
              <button onClick={() => setMode('new')}
                className={`text-sm font-medium px-3 h-7 rounded transition-colors ${mode === 'new' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="chapter-mode-new">New chapter</button>
            </div>
          </div>

          {!selectedClassId ? (
            <div className="py-6 text-center text-sm text-slate-600 bg-slate-50 rounded-lg border border-dashed border-slate-200">Choose a class above to see its chapters</div>
          ) : mode === 'pick' ? (
            loading ? (
              <ChapterListSkeleton />
            ) : chapters.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-600 bg-slate-50 rounded-lg border border-dashed border-slate-200">No chapters yet. Switch to <span className="font-medium">New chapter</span></div>
            ) : (
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {chapters.map(ch => {
                  const active = selectedLessonId === ch.id;
                  return (
                    <button key={ch.id} onClick={() => setSelectedLessonId(ch.id)}
                      className={`w-full flex items-center gap-3 rounded-lg border px-3 h-14 text-left transition-colors ${active ? 'border-[#34A853] bg-[#34A853]/5' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                      data-testid={`chapter-pick-${ch.id}`}>
                      {active ? <CheckCircle className="w-4 h-4 text-[#1A7F37] flex-shrink-0" /> : <BookOpen className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{ch.title}</p>
                        <p className="text-xs text-slate-500 tabular-nums">{ch.topic_count || 0} topic{(ch.topic_count || 0) !== 1 ? 's' : ''}{ch.latest_topic_date ? ` · last on ${new Date(ch.latest_topic_date).toLocaleDateString()}` : ''}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            <div className="rounded-lg border border-slate-200 p-4">
              <label htmlFor="chapter-new-title" className="text-sm font-medium text-slate-900 mb-2 block">New chapter name</label>
              <input id="chapter-new-title" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Chapter 4 — Thermodynamics"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#34A853]/40 focus:border-[#34A853] bg-white"
                data-testid="chapter-new-title" autoFocus />
              <p className="text-sm text-slate-600 mt-2">Chapters group your daily topics. You can rename or reorder them later.</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-2">
          {item.detected_class_id ? (
            <button onClick={() => onSubmit({ class_id: null })} className="text-sm text-slate-600 hover:text-red-700 transition-colors" data-testid="route-clear">Clear routing</button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className={SECONDARY_BTN}>Cancel</button>
            <button onClick={submit} disabled={!canSubmit || saving}
              className={PRIMARY_BTN}
              data-testid="route-confirm">{saving ? 'Saving…' : 'Save routing'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────── Settings — customize the session card ───────────── */
function SettingsSection({ fields, onChange }) {
  return (
    <div className="px-8 py-8 max-w-7xl mx-auto space-y-8 [&>*]:max-w-3xl">
      <PageHeading title="Settings" description="Choose what shows on your Deskey Session cards" />

      <div className={`${CARD} p-6`}>
        <h3 className="text-base font-semibold text-slate-900">Session card fields</h3>
        <p className="mt-1 text-sm text-slate-600">Class and chapter always show. Saved to your account.</p>
        <div className="mt-4 divide-y divide-slate-100">
          {CARD_FIELD_DEFS.map(f => {
            const on = !!fields[f.key];
            return (
              <div key={f.key} className="flex items-center justify-between h-16">
                <div>
                  <p className="text-sm font-medium text-slate-900">{f.label}</p>
                  <p className="text-sm text-slate-500">{on ? 'Shown on the card' : 'Hidden'}</p>
                </div>
                <button
                  role="switch" aria-checked={on} aria-label={f.label}
                  onClick={() => onChange({ [f.key]: !on })}
                  data-testid={`card-field-toggle-${f.key}`}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#34A853] ${on ? 'bg-[#34A853]' : 'bg-slate-200'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${on ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** "1 subject" / "2 subjects" — the count is shown separately. */
function plural(n, one, many = `${one}s`) {
  return n === 1 ? one : many;
}

function relativeTime(iso) {
  if (!iso) return 'just now';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   SUBJECT VIEW — Content list inside a subject
   ═══════════════════════════════════════════════════════════════════════════ */
function SubjectView({ subject, drafts, setActiveDraft, classes, reload }) {
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const queryClient = useQueryClient();
  const contentKey = ['teacher', 'subject-content', subject.id];
  const contentQ = useQuery({
    queryKey: contentKey,
    queryFn: () => api(`/studio/subjects/${subject.id}/content`).then(res => res.content || []),
    meta: { silent: true },
  });
  const subjectDrafts = contentQ.data ?? drafts;
  const loadingContent = contentQ.isPending;
  const setSubjectDrafts = (update) => queryClient.setQueryData(contentKey, (old = drafts) => update(old));

  const createDraft = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const draft = await api('/studio/drafts', { method: 'POST', body: JSON.stringify({ title: newTitle.trim(), subject_id: subject.id }) });
      if (draft.id) { setSubjectDrafts(prev => [draft, ...prev]); setActiveDraft(draft); setNewTitle(''); toast.success('Draft created'); reload(); }
    } catch (e) { notifyError(e, "Couldn't create the draft."); }
    setCreating(false);
  };

  const deleteDraft = async (id) => {

    if (!(await confirmDialog({ title: 'Delete draft', message: "Delete this draft? This can't be undone.", danger: true, confirmLabel: 'Delete' }))) return;
    try { await api(`/studio/drafts/${id}`, { method: 'DELETE' }); setSubjectDrafts(prev => prev.filter(d => d.id !== id)); toast.success('Deleted'); reload(); }
    catch (e) { notifyError(e, "Couldn't delete the draft."); }
  };

  return (
    <div className="px-8 py-8 max-w-7xl mx-auto space-y-8">
      <PageHeading
        title={subject.name}
        description={`${subjectDrafts.length} ${plural(subjectDrafts.length, 'item')} · ${subjectDrafts.filter(d => d.status === 'published').length} published · record new lessons from the Deskey app`}
      />

      <div className={`${CARD} p-5`}>
        <label htmlFor="new-draft-title" className="block text-sm font-medium text-slate-900 mb-2">New lesson</label>
        <div className="flex gap-3">
          <Input id="new-draft-title" value={newTitle} onChange={e => setNewTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && createDraft()}
            placeholder="Lesson title" className="h-10 flex-1 rounded-lg text-sm" data-testid="new-draft-title" />
          <Button onClick={createDraft} disabled={creating || !newTitle.trim()} className={PRIMARY_BTN} data-testid="create-draft-btn">
            <Plus className="w-4 h-4 mr-2" />{creating ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </div>

      <div>
        <div className="mb-4"><SectionTitle as="h3" title="Content" count={subjectDrafts.length} /></div>
        {subjectDrafts.length === 0 && loadingContent ? (
          <ContentRowsSkeleton rows={4} />
        ) : subjectDrafts.length === 0 ? (
          <EmptyState title="No content yet for this subject" />
        ) : (
          <div className={`${CARD} divide-y divide-slate-100`}>
            {subjectDrafts.map(d => {
              const ready = d.has_framework || d.framework;
              const status = d.status === 'published' ? { tone: 'done', label: 'Published' } : ready ? { tone: 'todo', label: 'Framework ready' } : { tone: 'todo', label: 'Draft' };
              return (
                <div key={d.id} onClick={() => setActiveDraft(d)}
                  className="px-5 h-16 flex items-center gap-4 cursor-pointer hover:bg-slate-50 transition-colors group" data-testid={`draft-${d.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{d.title}</p>
                    {d.key_topics?.length > 0 && <p className="text-sm text-slate-500 truncate">{d.key_topics.slice(0, 2).join(', ')}</p>}
                  </div>
                  <StatusBadge tone={status.tone} label={status.label} />
                  <button onClick={e => { e.stopPropagation(); deleteDraft(d.id); }} aria-label="Delete draft" className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-slate-400 hover:text-red-600 transition-[color,opacity] p-1"><Trash2 className="w-4 h-4" /></button>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FRAMEWORK SECTION — Expandable with inline edit + AI chat target
   ═══════════════════════════════════════════════════════════════════════════ */



/* ═══════════════════════════════════════════════════════════════════════════
   MY CLASSES / PUBLISHED / SCORES
   ═══════════════════════════════════════════════════════════════════════════ */
function NotificationBell({ token }) {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  // Only the tiny unread count is polled (for the red badge); the full list is
  // fetched when the teacher actually opens the bell.
  useEffect(() => {
    const loadCount = async () => {
      try {
        const c = await fetch(`${API}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
        setUnread(c?.count || 0);
      } catch { /* badge just stays as it was */ }
    };
    loadCount();
    const interval = setInterval(loadCount, 60000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(n => setNotifs(Array.isArray(n) ? n : []))
      .catch(() => { /* keep whatever is shown */ });
  }, [open, token]);

  const markRead = async (id) => {
    await fetch(`${API}/api/notifications/${id}/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/read-all`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  const typeIcon = (type) => {
    if (type?.includes('substitution') || type?.includes('substitute')) return { icon: Shield, color: '#3B82F6' };
    if (type?.includes('access')) return { icon: MessageSquare, color: '#F59E0B' };
    return { icon: Bell, color: '#64748b' };
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'} className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors" data-testid="notification-bell">
        <Bell className="w-5 h-5" />
        {unread > 0 && <span className="absolute top-0.5 right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-600 text-white text-xs leading-4 font-medium tabular-nums text-center">{unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
              {unread > 0 && <button onClick={markAllRead} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Mark all read</button>}
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {notifs.length === 0 ? (
                <div className="py-10 text-center"><p className="text-sm text-slate-500">No notifications</p></div>
              ) : notifs.slice(0, 20).map(n => {
                const { icon: Icon } = typeIcon(n.type);
                return (
                  <button key={n.id} onClick={() => { if (!n.read) markRead(n.id); }} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100" data-testid={`notif-${n.id}`}>
                    <Icon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{n.title}</p>
                      <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-xs text-slate-500 mt-1 tabular-nums">{new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#2563eb] flex-shrink-0 mt-2" aria-label="Unread" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MyClasses({ classes: legacyClasses, token }) {
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [hiddenCards, setHiddenCards] = useState(() => JSON.parse(localStorage.getItem('desky_hidden_classes') || '[]'));
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [substitutions, setSubstitutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ac, as2, subs] = await Promise.all([
          fetch(`${API}/api/classes/my-assigned`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
          fetch(`${API}/api/subjects/my-assigned`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
          fetch(`${API}/api/substitutions/my?include_ended=true`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        ]);
        setAssignedClasses(ac.classes || []);
        setAssignedSubjects(as2 || []);
        setSubstitutions(subs || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [token]);

  const hideCard = (id) => {
    const updated = [...hiddenCards, id];
    setHiddenCards(updated);
    localStorage.setItem('desky_hidden_classes', JSON.stringify(updated));
  };

  const deleteEndedSub = async (subId) => {

    if (!(await confirmDialog({ title: 'Delete substitution record', message: "Delete this substitution record? This can't be undone.", danger: true, confirmLabel: 'Delete' }))) return;
    await fetch(`${API}/api/substitutions/${subId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setSubstitutions(prev => prev.filter(s => s.id !== subId));
  };

  // Merge: assigned classes (active) + legacy classes not in assigned (shown as unassigned)
  const allClasses = [...assignedClasses.map(c => ({ ...c, _active: true }))];
  legacyClasses.forEach(c => {
    if (!allClasses.find(a => a.id === c.id)) allClasses.push({ ...c, _active: false });
  });
  const visibleClasses = allClasses.filter(c => !hiddenCards.includes(c.id));

  const activeSubs = substitutions.filter(s => s.status === 'active');
  const endedSubs = substitutions.filter(s => s.status !== 'active');

  return (
    <div className="px-8 py-8 max-w-7xl mx-auto space-y-8" data-testid="my-classes-section">
      <SectionTitle title="My Classes" />

      {loading ? <SubstitutionCardsSkeleton /> : (
        <>
          {/* Active Substitution Banner */}
          {activeSubs.length > 0 && (
            <div className={`${CARD} px-5 py-4 flex items-center gap-4`} data-testid="sub-active-banner">
              <div className="w-10 h-10 rounded-lg bg-blue-600/10 text-blue-800 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">{activeSubs.length} active {activeSubs.length > 1 ? 'substitutions' : 'substitution'}</p>
                <p className="text-sm text-slate-600">You are covering for other teachers. Post their content from Content Studio</p>
              </div>
            </div>
          )}

          {visibleClasses.length === 0 && activeSubs.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center"><Layers className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No classes assigned</p></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {visibleClasses.map(c => (
                <div key={c.id} className={`bg-white rounded-xl border p-4 relative group transition ${c._active ? 'border-slate-200 hover:shadow-md' : 'border-dashed border-slate-300 opacity-60'}`} data-testid={`class-card-${c.id}`}>
                  {!c._active && (
                    <button onClick={() => hideCard(c.id)} className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 hover:bg-red-50 transition" title="Hide card" data-testid={`hide-class-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                  )}
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: c._active ? `${G}14` : '#f1f5f9' }}>
                      <BookOpen className="w-4 h-4" style={{ color: c._active ? G : '#94a3b8' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{c.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{c.class_code}</p>
                    </div>
                    <span className={`text-xs font-semibold capitalize px-2 py-0.5 rounded-full ${c._active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                      {c._active ? 'Active' : 'Unassigned'}
                    </span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-slate-500"><span className="font-semibold text-slate-700">{c.student_count || 0}</span> Students</span>
                    <span className="text-slate-500"><span className="font-semibold text-slate-700">{c.lesson_count || 0}</span> Lessons</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Assigned Subjects */}
          {assignedSubjects.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Assigned Subjects</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {assignedSubjects.map(s => (
                  <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3" data-testid={`assigned-subject-${s.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${G}14` }}><BookOpen className="w-3.5 h-3.5" style={{ color: G }} /></div>
                    <div className="flex-1 min-w-0"><p className="text-xs font-semibold text-slate-900 truncate">{s.name}</p><p className="text-xs text-slate-500">{s.class_name}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Substitutions — Special Embossed UI */}
          {activeSubs.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Shield className="w-4 h-4 text-blue-500" /> Active Substitutions</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {activeSubs.map(sub => (
                  <div key={sub.id} className="relative rounded-xl p-4 border-2 border-blue-300 overflow-hidden group hover:shadow-lg transition" data-testid={`substitution-card-${sub.id}`}
                    style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 40%, #E0E7FF 100%)' }}>
                    {/* Decorative accent */}
                    <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, #3B82F6, #6366F1)' }} />
                    <div className="absolute top-3 right-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-500 text-white ">Substitute</span>
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3B82F6, #6366F1)' }}>
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{sub.subject_name}</p>
                        <p className="text-xs text-blue-700">{sub.class_name}</p>
                      </div>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Users className="w-3 h-3 text-blue-500" />
                        <span>Covering for <strong>{sub.original_teacher_name}</strong></span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Calendar className="w-3 h-3 text-blue-500" />
                        <span>{sub.start_date} &mdash; {sub.end_date}</span>
                      </div>
                      {sub.reason && (
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <FileText className="w-3 h-3 text-blue-400" />
                          <span className="italic">{sub.reason}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-blue-200/50">
                      <span className="text-xs text-blue-600 font-semibold bg-blue-100 px-2 py-0.5 rounded-full">{sub.lectures_posted || 0} lectures posted</span>
                      {!sub.accepted && (
                        <button
                          onClick={async () => {
                            try {
                              const { acceptSubstitution } = await import('./lib/surrogate');
                              const updated = await acceptSubstitution(sub.id);
                              toast.success('Assignment accepted');
                              setSubstitutions(prev => prev.map(s => s.id === sub.id ? { ...s, ...updated } : s));
                            } catch (e) { notifyError(e, 'Could not accept'); }
                          }}
                          className="ml-auto h-8 px-3 rounded-lg bg-[#1A7F37] hover:bg-[#166B2E] text-white text-sm font-medium transition-colors"
                          data-testid={`accept-substitution-${sub.id}`}
                        >Accept</button>
                      )}
                      {sub.accepted && (
                        <span className="ml-auto" data-testid={`accepted-badge-${sub.id}`}><StatusBadge tone="done" label="Accepted" /></span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ended/Unassigned Substitutions */}
          {endedSubs.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-400 mb-2 flex items-center gap-1.5">Past Substitutions</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {endedSubs.map(sub => (
                  <div key={sub.id} className="bg-slate-50 rounded-xl border border-slate-200 p-3 flex items-center gap-3 opacity-70 group" data-testid={`ended-sub-card-${sub.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-200"><Shield className="w-3.5 h-3.5 text-slate-500" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{sub.subject_name}</p>
                      <p className="text-xs text-slate-400">{sub.start_date} &mdash; {sub.end_date}</p>
                    </div>
                    <span className="text-xs text-slate-400 px-1.5 py-0.5 rounded bg-slate-200 capitalize">{sub.status}</span>
                    <button onClick={() => deleteEndedSub(sub.id)} className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition" title="Delete record" data-testid={`delete-ended-sub-${sub.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SubstitutionSection({ token, onActiveCount }) {
  const [tab, setTab] = useState('active');
  const [acceptingId, setAcceptingId] = useState(null);
  const [enteringId, setEnteringId] = useState(null);
  const [surrogateState, setSurrogateState] = useState(() => getSurrogateState());

  const queryClient = useQueryClient();
  const subsKey = ['teacher', 'substitutions'];
  const { data: subsData, isPending: loading, refetch: load } = useQuery({
    queryKey: subsKey,
    queryFn: async () => {
      const s = await fetch(`${API}/api/substitutions/my?include_ended=true`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      return Array.isArray(s) ? s : [];
    },
    meta: { silent: true },
  });
  const subs = subsData ?? [];
  const setSubs = (update) => queryClient.setQueryData(subsKey, (old = []) => update(old));
  useEffect(() => {
    if (subsData) onActiveCount?.(subsData.filter(x => x.status === 'active').length);
  }, [subsData, onActiveCount]);  // onActiveCount is a state setter — stable
  useEffect(() => { setSurrogateState(getSurrogateState()); }, []);

  const handleAccept = async (subId) => {
    setAcceptingId(subId);
    try {
      const updated = await acceptSubstitution(subId);
      toast.success('Assignment accepted — you can now enter Surrogate Mode');
      setSubs(prev => prev.map(s => (s.id === subId ? { ...s, ...updated } : s)));
    } catch (e) {
      notifyError(e, 'Could not accept');
    }
    setAcceptingId(null);
  };

  const handleEnter = async (subId) => {
    setEnteringId(subId);
    try {
      const res = await enterSurrogateMode(subId);
      toast.success(`Now acting for ${res.surrogate_for_name}`);
      window.location.reload();
    } catch (e) {
      notifyError(e, 'Could not enter surrogate mode');
    }
    setEnteringId(null);
  };

  const deleteEndedSub = async (subId) => {

    if (!(await confirmDialog({ title: 'Delete substitution record', message: "Delete this substitution record? This can't be undone.", danger: true, confirmLabel: 'Delete' }))) return;
    await fetch(`${API}/api/substitutions/${subId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const activeSubs = subs.filter(s => s.status === 'active');
  const endedSubs = subs.filter(s => s.status !== 'active');
  const today = new Date().toISOString().slice(0, 10);
  const isInWindow = (s) => s.start_date <= today && today <= s.end_date;

  return (
    <div className="px-8 py-8 max-w-7xl mx-auto space-y-8" data-testid="substitution-section">
      <PageHeading title="Substitutions" description="Accept an assignment, then enter Surrogate Mode to act as the original teacher" />

      {surrogateState.active && surrogateState.meta && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-center gap-3">
          <Shield className="w-4 h-4 text-amber-700 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">You&apos;re acting for {surrogateState.meta.surrogate_for_name}</p>
            <p className="text-sm text-amber-800">{surrogateState.meta.subject_name} · until {surrogateState.meta.end_date}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg w-fit" role="tablist">
        {[
          { id: 'active', label: 'Active', count: activeSubs.length },
          { id: 'ended', label: 'Past', count: endedSubs.length },
        ].map(t => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} className={`px-3 h-8 rounded text-sm font-medium transition-colors ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-testid={`sub-tab-${t.id}`}>
            {t.label} <span className="text-slate-500 tabular-nums">{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? <SubstitutionCardsSkeleton /> : null}

      {!loading && tab === 'active' && (
        <div className="space-y-4">
          {activeSubs.length === 0 ? (
            <EmptyState icon={Shield} title="No active substitutions" />
          ) : activeSubs.map(sub => {
            const inWindow = isInWindow(sub);
            const canEnter = sub.accepted && inWindow && !sub.revoked_at && !surrogateState.active;
            return (
              <div key={sub.id} className={`${CARD} p-5 space-y-4`} data-testid={`sub-card-${sub.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-slate-900">{sub.subject_name}</p>
                    <p className="text-sm text-slate-600">{sub.class_name} · Covering for {sub.original_teacher_name} · <span className="tabular-nums">{sub.start_date} to {sub.end_date}</span></p>
                  </div>
                  {sub.accepted ? (
                    <span data-testid={`accepted-badge-${sub.id}`}><StatusBadge tone="done" label="Accepted" /></span>
                  ) : (
                    <StatusBadge tone="neutral" label="Pending" />
                  )}
                </div>

                {sub.reason && (
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2" data-testid={`sub-reason-${sub.id}`}>
                    &ldquo;{sub.reason}&rdquo;
                  </p>
                )}

                {!inWindow && (
                  <p className="text-sm text-amber-800 tabular-nums">
                    {today < sub.start_date ? `Starts ${sub.start_date}` : `Ended ${sub.end_date}`}
                  </p>
                )}

                <div className="flex gap-2">
                  {!sub.accepted ? (
                    <Button onClick={() => handleAccept(sub.id)} disabled={acceptingId === sub.id || !inWindow} className={PRIMARY_BTN} data-testid={`accept-substitution-${sub.id}`}>
                      {acceptingId === sub.id ? 'Accepting…' : 'Accept'}
                    </Button>
                  ) : (
                    <Button onClick={() => handleEnter(sub.id)} disabled={!canEnter || enteringId === sub.id} className={PRIMARY_BTN} data-testid={`enter-surrogate-${sub.id}`}>
                      <Shield className="w-4 h-4 mr-2" />
                      {enteringId === sub.id ? 'Entering…' : surrogateState.active ? 'Exit current session first' : 'Enter Surrogate Mode'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && tab === 'ended' && (
        endedSubs.length === 0 ? (
          <EmptyState icon={Shield} title="No past substitutions" />
        ) : (
          <div className={`${CARD} divide-y divide-slate-100`}>
            {endedSubs.map(sub => (
              <div key={sub.id} className="px-5 h-16 flex items-center gap-3 group" data-testid={`ended-sub-${sub.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{sub.subject_name}</p>
                  <p className="text-sm text-slate-500 truncate">{sub.class_name} · {sub.original_teacher_name} · <span className="tabular-nums">{sub.start_date} to {sub.end_date}</span></p>
                </div>
                <StatusBadge tone="neutral" label={`${(sub.status || "").charAt(0).toUpperCase()}${(sub.status || "").slice(1)}`} />
                <button onClick={() => deleteEndedSub(sub.id)} aria-label="Delete record" className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-[color,background-color,opacity]" data-testid={`delete-sub-${sub.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function PublishedContent() {
  const { data: items = [], isPending: loading } = useQuery({
    queryKey: ['teacher', 'published-drafts'],
    queryFn: () => api('/studio/drafts?status=published').then(r => r.drafts || []),
    meta: { errorMessage: "Couldn't load published content." },
  });
  return (
    <div className="px-8 py-8 max-w-7xl mx-auto space-y-8">
      <PageHeading title="Published" description="Lessons your students can see on their Student Desk" />
      {loading ? <ContentRowsSkeleton label="Loading published lessons…" testId="published-loading" /> : items.length === 0 ? <EmptyState icon={CheckCircle} title="No published lessons yet" /> : (
        <div className={`${CARD} divide-y divide-slate-100`}>{items.map(d => (
          <div key={d.id} className="px-5 h-16 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{d.title}</p>
              <p className="text-sm text-slate-500 tabular-nums flex items-center gap-1.5"><Clock className="w-4 h-4 text-slate-400" />{d.published_at_label ? `Published ${d.published_at_label}` : 'Published'}</p>
            </div>
          </div>
        ))}</div>
      )}
    </div>
  );
}
